import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CostCenterUserTableModel } from 'src/app/models/tables/authentication/cost-center-user-table.model';
import { CostCenterUserService } from 'src/app/services/authentication/cost-center-user.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-cost-center-user',
  templateUrl: './cost-center-user.component.html',
  styleUrls: ['./cost-center-user.component.scss'],
  standalone: false
})
export class CostCenterUserComponent implements OnInit {

  private readonly config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly costCenterUserService = inject(CostCenterUserService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendant: boolean = true;
  columnSort: string | null = null;
  data: PagedResponseModel<CostCenterUserTableModel> = new PagedResponseModel<CostCenterUserTableModel>();
  disabledButton: boolean = false;
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  output$: Subject<any>;
  page: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Modificar',
      icon: 'pi pi-sync',
      action: 'edit'
    },
  ];
  userContract: UserModel = new UserModel();

  async ngOnInit(): Promise<void> {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.userContract = inputs.userModel;
    });
    await Promise.all([
      this.getColumnsCostCenterUserAsync(),
      this.getCostCenterUsers()
    ]);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getColumnsCostCenterUserAsync() {
    try {
      this.header = await firstValueFrom(this.costCenterUserService.getColumnsCostCenterUserAsync(this.userContract.userId));
    } catch (error) {
      console.error('Error al obtener las columnas para acceso a centros de costos.', error);
    }
  }


  async getCostCenterUsers() {
    try {
      this.data = await firstValueFrom(this.costCenterUserService.getCostCenterUsers(this.userContract.userId, this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al obtener los permisos por usuarios.', error);
    }
  }

  async onAction(event: { action: string, item?: CostCenterUserTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      const accepted = await this.confirmDialogService.confirm('¿Realmente desea modificar el acceso al centro de costos para este usuario?');
      if (accepted) {
        try {
          await firstValueFrom(this.costCenterUserService.saveCostCenterUser(this.userContract.userId, event.item.costCenterId));
          this.toastHandlerService.showToast('success', 'El acceso al centro de costos se actualizo correctamente.');
          await this.getCostCenterUsers();
        } catch (error) {
          console.error('Error al guardar el acceso al centro de costos para el usuario.', error);
        }
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getCostCenterUsers();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
